import { AxiosError } from 'axios'
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useState } from 'react'
import * as yup from 'yup'
import { Audit, AuditType, Sku } from 'src/api/models'
import { useForm, MapSelectField, QuerySelectField, TextareaField, TextField } from 'src/forms'
import { setApiErrors } from 'src/forms/errors'
import { skuit } from 'src/services/skuit'
import { useQueryPaginator } from 'src/components/Table'
import { handleErrorMessage, Notification } from 'src/components/Notification'
import toast from 'react-hot-toast'

const useStyles = makeStyles(() => ({
  form: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
}))

interface AuditProps {
  clients: string[]
  facilities: string[]
  places: string[]
  skus: string[]
}

const auditSchema = yup.object().shape({
  name: yup.string().required().label('Name'),
  audit_type: yup.string().required().label('Type'),
  notes: yup.string().optional().nullable().label('Notes'),
  clients: yup.array().of(yup.string().client()).optional().label('Clients'),
  facilities: yup.array().of(yup.string().facility()).optional().label('Facilities'),
  places: yup.array().of(yup.string().place()).optional().label('Places'),
  skus: yup.array().of(yup.string().sku()).optional().label('Skus'),
})

interface Props {
  open: boolean
  handleClose: () => void
  onSave?: () => void
  update?: boolean
  audit?: Audit
}

interface SkuDisplayProps {
  obj: Sku
}

const SkuDisplay = (props: SkuDisplayProps) => {
  const { obj } = props

  return (
    <Card>
      <CardContent>
        <Typography>
          {obj.client.name} | {obj.name}
        </Typography>
      </CardContent>
    </Card>
  )
}

export const AuditDialog = (props: Props) => {
  const { open, handleClose, onSave, update, audit } = props
  const classes = useStyles()
  const [auditType, setAuditType] = useState<string | undefined>(audit?.auditType)

  const facilityPaginator = useQueryPaginator('facility-list', skuit.sc.facilities.list)
  const skuPaginator = useQueryPaginator('sku-list', skuit.sc.skus.list)

  const onSubmit = (data: AuditProps) => {
    let func = skuit.sc.audits.create
    let action = 'creat'
    if (update) {
      func = (data: AuditProps) => skuit.sc.audits.patch(audit?.guid || '', data)
      action = 'updat'
    }
    func(data)
      .then((newAudit: Audit) => {
        handleClose()
        onSave?.()
        toast.success(() => (
          <Notification
            message={`Successfully ${action}ed Audit: ${newAudit.name}`}
            newObject={newAudit}
          />
        ))
      })
      .catch((error: AxiosError) => {
        handleErrorMessage(error, action, 'Audit')
        setApiErrors(formData.setError, error)
      })
  }

  const { Form, data: formData } = useForm(auditSchema)

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Form formProps={{ className: classes.form }} onSubmit={onSubmit}>
        <DialogTitle>{update ? 'Update' : 'Create'} Audit</DialogTitle>
        <DialogContent>
          <QuerySelectField
            name="facility"
            label="Facility"
            paginator={facilityPaginator}
            defaultValue={audit?.facility}
            disabled={audit?.facility && update && audit?.status !== Audit.Status.unstarted}
          />
          <TextField name="name" label="Name" defaultValue={audit?.name} />
          <TextareaField name="notes" label="Notes" defaultValue={audit?.notes} />
          <MapSelectField
            name="audit_type"
            label="Type"
            options={AuditType}
            defaultValue={audit?.auditType || 'pl'}
            onSelected={(auditType) => setAuditType(auditType)}
            disabled={update && audit?.status !== Audit.Status.unstarted}
          />
          <QuerySelectField
            name="sku"
            label="SKU"
            paginator={skuPaginator}
            defaultValue={audit?.sku}
            renderOption={(obj) => <SkuDisplay obj={obj} />}
            disabled={
              !auditType ||
              auditType !== 'sk' ||
              (update && audit?.status !== Audit.Status.unstarted)
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained" autoFocus>
            Save
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
